import { useCallback } from "react";
import { ConsolidationGroupResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useEntities } from "@hooks/useEntities";
import { resolveConsolidatedGlDrillDownLedger } from "./consolidatedGlDrillDown.lib";
import { AccountingReportFilters } from "./types";

interface Params {
    consolidationGroup: ConsolidationGroupResponse | undefined;
    isConsolidated: boolean;
    ledgerBasis: AccountingLedgerBasis | undefined;
}

interface GlDrillDownTarget {
    filters: AccountingReportFilters;
    ledgerBasis: AccountingLedgerBasis | undefined;
}

/**
 * A consolidated report renders every entity on the group's configured ledger,
 * so its GL drill-down must open that ledger rather than the report's own
 * (parent) ledger or the tab's last-used basis.
 */
export function useConsolidatedGlDrillDown({
    consolidationGroup,
    isConsolidated,
    ledgerBasis,
}: Params) {
    const entities = useEntities();

    return useCallback(
        ({
            filters,
            entityIds,
        }: {
            filters: AccountingReportFilters;
            entityIds: number[];
        }): GlDrillDownTarget => {
            if (!isConsolidated || !consolidationGroup) {
                return { filters, ledgerBasis };
            }
            const entityUuids = entityIds.flatMap((entityId) => {
                const entity = entities.find(({ id }) => id === entityId);
                return entity ? [entity.uuid] : [];
            });
            const resolved = resolveConsolidatedGlDrillDownLedger({
                group: consolidationGroup,
                entityUuids,
            });
            return {
                filters: { ...filters, ledgerId: resolved.ledgerId },
                ledgerBasis: resolved.ledgerBasis,
            };
        },
        [consolidationGroup, entities, isConsolidated, ledgerBasis],
    );
}
